import { Form } from "../../../../../../../shared/frontend/ui";
import React from "react";
import type { SetURLSearchParams } from "react-router-dom";
import type { ApiClient } from "../../../app/api/client";
import { CHANNEL_EMAIL_LC,CHANNEL_LETTER_LC } from "../../../app/constants/communicationChannel";
import type { I18nContextValue } from "../../../app/i18n/useI18n";
import type { PersonDto } from "../types";
import type { Feedback } from "../../../../../../../shared/frontend/feedback/useFeedback";

const WARNING_DURATION_SECONDS = 8;

export type PersonCommunicationFormValues = {
  templateId?: string | null;
  channel: string;
  subject?: string | null;
  documentTitle?: string | null;
  body: string;
  studioId?: string | null;
  sendEmail?: boolean;
  sendPrint?: boolean;
  sendApi?: boolean;
  integrationId?: string | null;
  contractPackageId?: string | null;
  restPeriodId?: string | null;
};

export type SendCommunicationOptions = {
  saveOnly?: boolean;
};

type UsePersonCommunicationActionsArgs = {
  api: ApiClient;
  text: I18nContextValue["text"];
  message: Feedback;
  person: PersonDto | null;
  allowedStudios: Array<{ id: string }>;
  setSearchParams: SetURLSearchParams;
  onDocumentsChanged: () => void;
};

export function usePersonCommunicationActions({
  api,
  text,
  message,
  person,
  allowedStudios,
  setSearchParams,
  onDocumentsChanged
}: UsePersonCommunicationActionsArgs) {
  const [communicationMode, setCommunicationMode] = React.useState<"list" | "create">("list");
  const [communicationSaving, setCommunicationSaving] = React.useState(false);
  const [communicationForm] = Form.useForm<PersonCommunicationFormValues>();

  const openCommunication = React.useCallback((channelOverride?: "email" | "letter") => {
    const studioId = person?.homeStudioId || allowedStudios[0]?.id || "";
    const channel = channelOverride || "email";

    communicationForm.setFieldsValue({
      channel,
      studioId: studioId || null,
      templateId: null,
      subject: "",
      documentTitle: "",
      body: "",
      sendEmail: channel === CHANNEL_EMAIL_LC ? true : (channel === CHANNEL_LETTER_LC && person?.email ? true : false),
      sendPrint: false,
      sendApi: false,
      integrationId: null,
      contractPackageId: null
    });

    setSearchParams({ tab: "files" });
    setCommunicationMode("create");
  }, [allowedStudios, communicationForm, person, setSearchParams]);

  const closeCommunication = React.useCallback(() => {
    setCommunicationMode("list");
    communicationForm.resetFields();
  }, [communicationForm]);

  const sendCommunication = React.useCallback(async (options?: SendCommunicationOptions) => {
    if (!person) return;

    await communicationForm.validateFields(["channel", "studioId"]);

    // Alle Kommunikationsfelder können aus dem eingeblendeten Einstellungsbereich stammen;
    // getFieldsValue(true) liest deshalb den vollständigen Form-Store.
    const values = communicationForm.getFieldsValue(true);
    const channel = values.channel || CHANNEL_EMAIL_LC;
    const studioId = values.studioId || person.homeStudioId || allowedStudios[0]?.id;

    if (!studioId) throw new Error(text("Studio is required"));

    const subject = values.subject || "";
    const contractPackageId: string | null = values.contractPackageId || null;
    const restPeriodId: string | null = values.restPeriodId || null;

    setCommunicationSaving(true);
    try {
      const saveOnly = options?.saveOnly === true;
      const sendEmail = !saveOnly && values.sendEmail === true && !!person.email;
      const sendPrint = values.sendPrint === true;
      const sendApi = !saveOnly && values.sendApi === true;

      if (!saveOnly && channel === CHANNEL_EMAIL_LC && !person.email) {
        throw new Error(text("No email address on file."));
      }

      const kind = restPeriodId
        ? "rest-period-confirmation"
        : contractPackageId ? "cancellation-confirmation" : "free-message";
      const referenceId = restPeriodId || contractPackageId || person.id;
      const result = await api.post<{
        documentId?: string | null;
        emailDocumentId?: string | null;
        outboundMessageId?: string | null;
        warnings: string[];
      }>(`/api/v1/system-documents/${kind}/send`, {
        referenceId,
        variantId: values.templateId || null,
        language: "de",
        subject: subject || null,
        sendEmail: saveOnly ? false : channel === CHANNEL_EMAIL_LC ? !!person.email : sendEmail,
      });

      result.warnings.forEach((warning) => message.warning(warning, WARNING_DURATION_SECONDS));
      const printOnly = sendPrint && !sendEmail && !sendApi && !!result.documentId;
      message.success(saveOnly || printOnly ? text("Saved.") : text("Document created and sent"));
      closeCommunication();
      onDocumentsChanged();
      window.dispatchEvent(new CustomEvent("person-documents-changed", { detail: { personId: person.id } }));
      if (restPeriodId) {
        window.dispatchEvent(new CustomEvent("person-rest-periods-changed", { detail: { personId: person.id, restPeriodId } }));
      }
    } finally {
      // Der Fehler wird bewusst nicht behandelt: der Aufrufer zeigt ihn an. `finally` sorgt dafür,
      // dass der Speicher-Riegel auch dann fällt.
      setCommunicationSaving(false);
    }
  }, [allowedStudios, api, closeCommunication, communicationForm, message, onDocumentsChanged, person, text]);

  return {
    communicationMode,
    communicationSaving,
    communicationForm,
    openCommunication,
    closeCommunication,
    sendCommunication,
    setCommunicationMode
  };
}
